import React, { useEffect, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Link as MuiLink,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import CustomerServiceRepSelector from '~/components/LuxLoyalty/CustomerServiceRepSelector';

import { LUXURY_ESCAPES } from '~/consts/brands';

import { useEnrolLuxLoyaltyAccount } from '~/hooks/apis/loyalty/useEnrolLuxLoyaltyAccount';

import { getInfiniteOnboardingEmailWarning } from '~/services/LuxLoyaltyService';
import UsersService from '~/services/UsersService';

import ConfirmInfiniteEnrolmentDialog from './ConfirmInfiniteEnrolmentDialog';
import CreateInfiniteExistingAccountStep from './CreateInfiniteExistingAccountStep';
import {
  CONCIERGE_REP_FIELD_LABEL,
  CONCIERGE_REP_HELPER_TEXT,
  CONCIERGE_REP_REQUIRED_ERROR,
  type CreateInfiniteOutcome,
  DEFAULT_COUNTRY_CODE,
  DEFAULT_PHONE_PREFIX,
  type EnrolmentPath,
  PHONE_PREFIX_OPTIONS,
  generateRandomPassword,
  isLikelyEmail,
} from './createInfiniteAccountShared';
import { useConciergeRepLabel } from './useConciergeRepLabel';
import {
  getRegisterCountryCodeError,
  isRegisterCountrySelectionReady,
  useInfiniteRegisterCountryOptions,
} from './useInfiniteRegisterCountryOptions';

interface NewAccountFormState {
  givenName: string;
  surname: string;
  email: string;
  countryCode: string;
  phonePrefix: string;
  phone: string;
}

const NEW_ACCOUNT_EMPTY_STATE: NewAccountFormState = {
  givenName: '',
  surname: '',
  email: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  phonePrefix: DEFAULT_PHONE_PREFIX,
  phone: '',
};

/**
 * Infinite enrolment tab: upgrade an existing LE customer or register a new
 * customer and enrol them at Infinite. Layout matches other Société admin forms
 * such as {@link LuxLoyaltyEnrolPanel}.
 */
export default function CreateInfiniteAccountTab() {
  const [activeStep, setActiveStep] = useState(0);
  const [enrolmentPath, setEnrolmentPath] = useState<EnrolmentPath | null>(null);
  const [newAccountForm, setNewAccountForm] = useState<NewAccountFormState>(NEW_ACCOUNT_EMPTY_STATE);
  const [csRepId, setCsRepId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [outcome, setOutcome] = useState<CreateInfiniteOutcome | null>(null);

  const conciergeRepLabel = useConciergeRepLabel(csRepId);
  const {
    options: countryOptions,
    isLoading: countryOptionsLoading,
    isError: countryOptionsError,
  } = useInfiniteRegisterCountryOptions();
  const countryValidationState = { isLoading: countryOptionsLoading, isError: countryOptionsError };

  useEffect(() => {
    if (countryOptionsLoading || countryOptions.length === 0) {
      return;
    }

    setNewAccountForm((prev) => {
      const current = prev.countryCode.trim();
      if (countryOptions.some((option) => option.value === current)) {
        return prev;
      }

      const preferred = countryOptions.find((option) => option.value === DEFAULT_COUNTRY_CODE);
      return { ...prev, countryCode: preferred?.value ?? countryOptions[0].value };
    });
  }, [countryOptions, countryOptionsLoading]);

  const trimmedNewAccount: NewAccountFormState = {
    givenName: newAccountForm.givenName.trim(),
    surname: newAccountForm.surname.trim(),
    email: newAccountForm.email.trim(),
    countryCode: newAccountForm.countryCode.trim(),
    phonePrefix: newAccountForm.phonePrefix.trim(),
    phone: newAccountForm.phone.trim(),
  };

  const newAccountErrors = {
    givenName: !trimmedNewAccount.givenName ? 'First name is required.' : null,
    surname: !trimmedNewAccount.surname ? 'Last name is required.' : null,
    email: !trimmedNewAccount.email
      ? 'Email is required.'
      : !isLikelyEmail(trimmedNewAccount.email)
        ? 'Enter a valid email address.'
        : null,
    countryCode: getRegisterCountryCodeError(trimmedNewAccount.countryCode, countryOptions, countryValidationState),
    phonePrefix: !trimmedNewAccount.phonePrefix ? 'Country code is required.' : null,
    phone: !trimmedNewAccount.phone ? 'Phone is required.' : null,
    csRep: csRepId ? null : CONCIERGE_REP_REQUIRED_ERROR,
  };
  const newAccountHasErrors = Object.values(newAccountErrors).some(Boolean);
  const isNewAccountCountryReady = isRegisterCountrySelectionReady(
    trimmedNewAccount.countryCode,
    countryOptions,
    countryValidationState,
  );
  const canSubmitNewAccount = !isSubmitting && isNewAccountCountryReady;

  const enrolMutation = useEnrolLuxLoyaltyAccount();

  const resetNewAccountForm = () => {
    setNewAccountForm(NEW_ACCOUNT_EMPTY_STATE);
    setCsRepId(null);
    setSubmitted(false);
  };

  const resetAfterSuccessfulEnrolment = () => {
    resetNewAccountForm();
    setActiveStep(0);
    setEnrolmentPath(null);
  };

  const handleBackToPathStep = () => {
    setActiveStep(0);
    setSubmitted(false);
    resetNewAccountForm();
  };

  const handleContinueFromPath = () => {
    if (!enrolmentPath) {
      return;
    }
    setSubmitted(false);
    setActiveStep(1);
  };

  const handleNewAccountSubmitRequest = () => {
    if (isSubmitting) {
      return;
    }
    setSubmitted(true);
    if (newAccountHasErrors || !csRepId || !isNewAccountCountryReady) {
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleNewAccountSubmit = async () => {
    if (isSubmitting) {
      return;
    }
    setSubmitted(true);
    if (newAccountHasErrors || !csRepId || !isNewAccountCountryReady) {
      setConfirmDialogOpen(false);
      return;
    }
    setIsSubmitting(true);
    setOutcome(null);

    let createdCustomerId: string;

    try {
      const payload = {
        givenName: trimmedNewAccount.givenName,
        surname: trimmedNewAccount.surname,
        email: trimmedNewAccount.email,
        country_code: trimmedNewAccount.countryCode,
        phone_prefix: trimmedNewAccount.phonePrefix,
        phone: trimmedNewAccount.phone,
        password: generateRandomPassword(),
        brand: LUXURY_ESCAPES,
        email_verified: true,
        bypass_confirmation_email: true,
      };
      const created = await UsersService.registerUser(payload);
      const id = (created?.id_member as string | undefined) ?? (created?.memberId as string | undefined);
      if (!id) {
        throw new Error('Account registered but no customer id was returned by the auth service.');
      }
      createdCustomerId = id;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error.';
      setOutcome({ kind: 'error', message: `Failed to register customer: ${message}` });
      setIsSubmitting(false);
      setConfirmDialogOpen(false);
      return;
    }

    const customerName = `${trimmedNewAccount.givenName} ${trimmedNewAccount.surname}`;
    const customerEmail = trimmedNewAccount.email;
    try {
      const enrolResponse = await enrolMutation.mutateAsync({
        customerId: createdCustomerId,
        tier: 'infinite',
        customerServiceRepId: csRepId,
        isFirstTimeUser: true,
      });
      const onboardingEmailWarning = getInfiniteOnboardingEmailWarning(enrolResponse);
      if (onboardingEmailWarning) {
        setOutcome({
          kind: 'email-warning',
          customerName,
          customerId: createdCustomerId,
          message: onboardingEmailWarning,
        });
        resetAfterSuccessfulEnrolment();
        return;
      }
      setOutcome({
        kind: 'success',
        customerName,
        customerId: createdCustomerId,
        message: `${customerName} is now an Infinite member.`,
      });
      resetAfterSuccessfulEnrolment();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error.';
      setOutcome({ kind: 'partial', customerEmail, customerId: createdCustomerId, message });
    } finally {
      setIsSubmitting(false);
      setConfirmDialogOpen(false);
    }
  };

  return (
    <Stack direction="column" gap={3} maxWidth={640}>
      <Box>
        <Typography variant="h6" gutterBottom>
          Infinite enrolment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upgrade an existing Luxury Escapes customer to Infinite, or register a new customer and enrol them at the
          Infinite Société tier with a dedicated concierge representative.
        </Typography>
      </Box>

      {outcome && (
        <Alert
          severity={outcome.kind === 'success' ? 'success' : outcome.kind === 'error' ? 'error' : 'warning'}
          onClose={() => setOutcome(null)}
        >
          {outcome.kind === 'success' && (
            <>
              <AlertTitle>Enrolment complete</AlertTitle>
              <Typography variant="body2">{outcome.message}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Customer id:{' '}
                <MuiLink
                  component={Link}
                  to={`/users/${outcome.customerId}`}
                  underline="hover"
                  sx={{ fontWeight: 700 }}
                >
                  {outcome.customerId}
                </MuiLink>
              </Typography>
            </>
          )}
          {outcome.kind === 'email-warning' && (
            <>
              <AlertTitle>Account created and enrolled</AlertTitle>
              <Typography variant="body2">
                <strong>{outcome.customerName}</strong> is now an Infinite member, but the onboarding email needs
                attention.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Customer id:{' '}
                <MuiLink
                  component={Link}
                  to={`/users/${outcome.customerId}`}
                  underline="hover"
                  sx={{ fontWeight: 700 }}
                >
                  {outcome.customerId}
                </MuiLink>{' '}
                · {outcome.message}
              </Typography>
            </>
          )}
          {outcome.kind === 'partial' && (
            <>
              <AlertTitle>Account created — enrolment failed</AlertTitle>
              <Typography variant="body2">
                Customer <strong>{outcome.customerEmail}</strong> was created, but the Société enrolment step failed.
                Finish enrolment manually from the user detail page.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Customer id:{' '}
                <MuiLink
                  component={Link}
                  to={`/users/${outcome.customerId}`}
                  underline="hover"
                  sx={{ fontWeight: 700 }}
                >
                  {outcome.customerId}
                </MuiLink>{' '}
                · Reason: {outcome.message}
              </Typography>
            </>
          )}
          {outcome.kind === 'error' && (
            <>
              <AlertTitle>Could not complete enrolment</AlertTitle>
              <Typography variant="body2">{outcome.message}</Typography>
            </>
          )}
        </Alert>
      )}

      {activeStep === 0 && (
        <>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Enrolment path *
            </Typography>
            <RadioGroup
              value={enrolmentPath ?? ''}
              onChange={(event) => setEnrolmentPath(event.target.value as EnrolmentPath)}
            >
              <FormControlLabel
                value="existing"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Existing account enrolment &amp; upgrade
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Find an existing Luxury Escapes customer, enrol them in Société at Infinite if needed, or upgrade
                      their current Société tier to Infinite.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1.5 }}
              />
              <FormControlLabel
                value="new"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      New Luxury Escapes account
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Register a new customer and enrol them at Infinite in one flow. The password is generated
                      automatically; they&apos;ll reset it via the standard forgot-password flow.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </RadioGroup>
          </Box>

          <Box>
            <Button variant="contained" color="primary" onClick={handleContinueFromPath} disabled={!enrolmentPath}>
              Continue
            </Button>
          </Box>
        </>
      )}

      {activeStep === 1 && enrolmentPath === 'existing' && (
        <CreateInfiniteExistingAccountStep
          submitted={submitted}
          onSubmitted={() => setSubmitted(true)}
          onOutcome={(nextOutcome) => {
            setOutcome(nextOutcome);
            if (nextOutcome.kind === 'success' || nextOutcome.kind === 'email-warning') {
              setActiveStep(0);
              setEnrolmentPath(null);
            }
          }}
          onBack={handleBackToPathStep}
        />
      )}

      {activeStep === 1 && enrolmentPath === 'new' && (
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            New accounts are registered under Luxury Escapes and enrolled at the Infinite tier.
          </Typography>

          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First name"
                value={newAccountForm.givenName}
                onChange={(event) => setNewAccountForm({ ...newAccountForm, givenName: event.target.value })}
                required
                fullWidth
                error={submitted && !!newAccountErrors.givenName}
                helperText={submitted ? (newAccountErrors.givenName ?? ' ') : ' '}
                disabled={isSubmitting}
              />
              <TextField
                label="Last name"
                value={newAccountForm.surname}
                onChange={(event) => setNewAccountForm({ ...newAccountForm, surname: event.target.value })}
                required
                fullWidth
                error={submitted && !!newAccountErrors.surname}
                helperText={submitted ? (newAccountErrors.surname ?? ' ') : ' '}
                disabled={isSubmitting}
              />
            </Stack>

            <TextField
              label="Email"
              value={newAccountForm.email}
              onChange={(event) => setNewAccountForm({ ...newAccountForm, email: event.target.value })}
              required
              fullWidth
              type="email"
              error={submitted && !!newAccountErrors.email}
              helperText={submitted ? (newAccountErrors.email ?? ' ') : ' '}
              disabled={isSubmitting}
            />

            <TextField
              select
              label="Country"
              value={newAccountForm.countryCode}
              onChange={(event) => setNewAccountForm({ ...newAccountForm, countryCode: event.target.value })}
              required
              fullWidth
              error={submitted && !!newAccountErrors.countryCode}
              helperText={
                countryOptionsLoading
                  ? 'Loading country options…'
                  : countryOptionsError
                    ? 'Could not load country options from the register schema.'
                    : countryOptions.length === 0
                      ? 'No country options are available.'
                      : submitted
                        ? (newAccountErrors.countryCode ?? ' ')
                        : 'Customer region, as on admin user creation.'
              }
              disabled={isSubmitting || countryOptionsLoading || countryOptionsError}
            >
              {countryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Country code"
                value={newAccountForm.phonePrefix}
                onChange={(event) => setNewAccountForm({ ...newAccountForm, phonePrefix: event.target.value })}
                required
                sx={{ minWidth: 260 }}
                error={submitted && !!newAccountErrors.phonePrefix}
                helperText={submitted ? (newAccountErrors.phonePrefix ?? ' ') : ' '}
                disabled={isSubmitting}
              >
                {PHONE_PREFIX_OPTIONS.map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Phone"
                value={newAccountForm.phone}
                onChange={(event) => setNewAccountForm({ ...newAccountForm, phone: event.target.value })}
                required
                fullWidth
                error={submitted && !!newAccountErrors.phone}
                helperText={submitted ? (newAccountErrors.phone ?? ' ') : ' '}
                disabled={isSubmitting}
              />
            </Stack>
          </Stack>

          <CustomerServiceRepSelector
            value={csRepId}
            onChange={setCsRepId}
            label={CONCIERGE_REP_FIELD_LABEL}
            required
            error={submitted && !csRepId}
            helperText={submitted && !csRepId ? newAccountErrors.csRep : CONCIERGE_REP_HELPER_TEXT}
            disabled={isSubmitting}
          />

          <Stack direction="row" spacing={2} alignItems="center">
            <Button variant="text" onClick={handleBackToPathStep} disabled={isSubmitting}>
              Back
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNewAccountSubmitRequest}
              disabled={!canSubmitNewAccount}
            >
              {isSubmitting ? 'Creating…' : 'Create account & enrol'}
            </Button>
          </Stack>

          <ConfirmInfiniteEnrolmentDialog
            open={confirmDialogOpen}
            onClose={() => setConfirmDialogOpen(false)}
            onConfirm={handleNewAccountSubmit}
            isSubmitting={isSubmitting}
            confirmLabel="Create account & enrol"
            message={
              <>
                This will create a new Luxury Escapes account and enrol{' '}
                <strong>
                  {trimmedNewAccount.givenName} {trimmedNewAccount.surname}
                </strong>{' '}
                into Infinite.
              </>
            }
            conciergeRepSummary={conciergeRepLabel}
          />
        </Stack>
      )}
    </Stack>
  );
}
